import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Cpu, Loader2, LogOut, Map as MapIcon, Play, Plus, ScanLine, SquareCheckBig, StopCircle, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PLAN_KINDS = {
  locations: { title: "Location-terv", request: "Új location-tervezés" },
  loctags: { title: "Loctag-terv", request: "Új loctag-tervezés" },
  size: { title: "Méret-felmérés", request: "Méret-felmérés (AI)" },
};

const JOB_STATUS_LABELS = {
  queued: "Sorban áll, az AI-worker hamarosan átveszi",
  processing: "Az AI dolgozik rajta",
  done: "Elkészült",
  error: "Hiba",
};

const WORKER_PROFILES = {
  tidy: "Nagyon rendes",
  normal: "Normál",
  messy: "Rendetlen",
};

const WORKER_ACTIONS = {
  web_login: "Webapp belépés",
  power_on: "Tracker bekapcsolás (boot)",
  device_login: "Device login (telefon)",
  tag_read: "Loctag olvasás",
  skip: "Loctag kihagyva",
  upload: "Felküldés",
  system_upload: "Rendszeresemények felküldése",
  device_logout: "Device kilépés",
  power_off: "Tracker kikapcsolás (shutdown)",
  web_logout: "Webapp kilépés",
  no_device: "Nincs szabad tracker",
};

const formatClock = (iso) => new Date(iso).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
const formatSeconds = (iso) => new Date(iso).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Egy előre legyártott tranzakció részletei a listában.
function actionDetail(action) {
  if (action.kind === "tag_read") return `${action.location || ""} · ${action.technical || ""}${action.count ? ` · ${action.count}× olvasva` : ""}`;
  if (action.kind === "skip") return `${action.location || ""} · ${action.technical || ""}`;
  if (action.kind === "upload") return `${action.items || 0} tétel`;
  if (action.kind === "power_on") return `${action.device_name || ""}${action.group ? ` · ${action.group}` : ""}`;
  if (["device_login", "device_logout", "power_off"].includes(action.kind)) return action.device_name || "";
  if (action.kind === "no_device") return action.group || "";
  return "";
}

const isActiveJob = (job) => ["queued", "processing"].includes(job?.status);

async function api(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "A művelet sikertelen");
  return data;
}

// Lenyitható panel fejléc: a cím a nyitást váltja, az opcionális művelet (pl. +) mellette marad.
function PanelHeader({ icon: Icon, title, open, onToggle, action = null }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <button onClick={onToggle} aria-expanded={open} className="flex flex-1 items-center gap-2 text-left text-sm font-semibold text-slate-100">
        <Icon size={16} className="text-teal-400" /> {title}
        <ChevronDown size={16} className={`ml-auto text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {action}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, count }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
        <Icon size={16} className="text-teal-400" /> {title}
      </div>
      {typeof count === "number" && <span className="text-xs text-slate-400">{count}</span>}
    </div>
  );
}

// Egy szimulált ember napja: felül ami megtörtént, a „most” jel alatt a még hátralévő tervek.
function WorkerTimeline({ worker, devices }) {
  const nowRef = useRef(null);
  const actions = worker.actions || [];
  const done = actions.filter((action) => action.done);
  const upcoming = actions.filter((action) => !action.done);
  const device = devices.find((item) => item.device_id === worker.device_id);
  useEffect(() => { nowRef.current?.scrollIntoView({ block: "center" }); }, [done.length]);
  const row = (action, index) => (
    <li key={`${action.kind}-${action.at}-${index}`} className={`grid grid-cols-[4.2rem_minmax(0,1fr)] gap-x-2 px-3 py-1 text-xs ${action.done ? (action.error ? "text-red-300" : "text-slate-500") : "text-slate-200"}`}>
      <span className="tabular-nums">{formatSeconds(action.at)}</span>
      <span className="min-w-0">
        <span className="font-medium">{action.done ? (action.error ? "✕ " : "✓ ") : ""}{WORKER_ACTIONS[action.kind] || action.kind}</span>
        {actionDetail(action) && <span className={action.done ? "text-slate-600" : "text-slate-400"}> · {actionDetail(action)}</span>}
      </span>
    </li>
  );
  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-slate-800 bg-slate-950/50">
      <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-100">{worker.user_name}</div>
          <div className="text-xs text-slate-500">{WORKER_PROFILES[worker.profile]}{device ? ` · ${device.device_name || device.device_id}` : ""}{worker.buffered ? ` · puffer: ${worker.buffered}` : ""}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${worker.enabled ? "bg-teal-500/15 text-teal-300" : "bg-slate-800 text-slate-400"}`}>{worker.enabled ? "fut" : "leállítva"}</span>
      </div>
      <ol className="min-h-0 flex-1 overflow-auto py-1">
        {done.map(row)}
        <li ref={nowRef} className="mx-3 my-1 flex items-center gap-2 text-xs font-semibold text-teal-400">
          <span className="h-px flex-1 bg-teal-500/60" /> most {formatClock(new Date().toISOString())} <span className="h-px flex-1 bg-teal-500/60" />
        </li>
        {upcoming.map(row)}
        {!actions.length && <li className="px-3 py-2 text-xs text-slate-500">Mára nincs tranzakció.</li>}
      </ol>
    </div>
  );
}

function SimulationBoard({ workers, devices }) {
  const virtual = devices.filter((device) => device.is_virtual);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <div className="flex flex-wrap gap-2">
        {virtual.map((device) => (
          <div key={device.device_id} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-xs">
            <span className={`h-2.5 w-2.5 rounded-full ${device.heartbeat_status === "online" ? "bg-emerald-400" : "bg-slate-600"}`} />
            <span className="font-medium text-slate-200">{device.device_name || device.device_id}</span>
            <span className="text-slate-500">{device.heartbeat_status === "online" ? "bekapcsolva" : "kikapcsolva"}{device.logged_in_user ? ` · ${device.logged_in_user}` : ""}</span>
          </div>
        ))}
        {!virtual.length && <p className="text-xs text-slate-500">Nincs szimulált device.</p>}
      </div>
      <div className="grid min-h-0 flex-1 gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gridAutoRows: "minmax(0, 68vh)" }}>
        {workers.map((worker) => <WorkerTimeline key={worker.id} worker={worker} devices={devices} />)}
        {!workers.length && <p className="text-sm text-slate-500">Még nincs szimulált ember. A bal oldalon, a „Szimulált emberek” + gombjával vehetsz fel.</p>}
      </div>
    </div>
  );
}

export default function SimulatorPage({ user, setUser }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  // A legutóbb választott cég felhasználónként megmarad, újratöltéskor azzal nyílik.
  const companyStorageKey = `tracesim.company.${user?.user_id || user?.email || "default"}`;
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(companyStorageKey) || ""; } catch { return ""; }
  });
  const [companyData, setCompanyData] = useState(null);
  const [rootId, setRootId] = useState("");
  const [rootData, setRootData] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const trackerName = "Virtual TraceMop Tracker";
  const [canvasMode, setCanvasMode] = useState("current");
  const [showLocations, setShowLocations] = useState(true);
  const [showLoctags, setShowLoctags] = useState(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedLoctagId, setSelectedLoctagId] = useState("");
  const [mopTags, setMopTags] = useState([]);
  const [selectedMopEpc, setSelectedMopEpc] = useState("");
  const [workerUserId, setWorkerUserId] = useState("");
  const [workerProfile, setWorkerProfile] = useState("normal");
  const [openPanels, setOpenPanels] = useState({ location: false, workers: false, devices: false, manual: false });
  const togglePanel = (name) => setOpenPanels((current) => ({ ...current, [name]: !current[name] }));
  const [workerFormOpen, setWorkerFormOpen] = useState(false);
  const [openWorkerId, setOpenWorkerId] = useState(null);
  const copyText = (text) => { navigator.clipboard?.writeText(text).catch(() => {}); };

  const selectedRoot = useMemo(
    () => companyData?.root_locations?.find((root) => String(root.id) === String(rootId)),
    [companyData, rootId]
  );
  const survey = rootData?.survey;
  const jobs = useMemo(() => rootData?.jobs || {}, [rootData?.jobs]);
  const activeJob = [jobs.locations, jobs.loctags].find(isActiveJob);
  const locationPlanReady = survey?.status === "preview" && survey?.proposal?.source === "ai";
  const loctagPlan = survey?.loctag_plan?.tags ? survey.loctag_plan : null;
  const loctagPlanReady = Boolean(loctagPlan && !loctagPlan.applied_at);
  const planPreviewUrl = canvasMode === "locations" ? survey?.preview_url : canvasMode === "loctags" ? survey?.loctag_preview_url : null;
  const sizeSurvey = survey?.size_survey?.total_area_m2 ? survey.size_survey : null;
  const previousJobs = useRef({});
  const currentLocations = useMemo(() => rootData?.locations || [], [rootData?.locations]);
  const currentLoctags = useMemo(() => rootData?.loctags || [], [rootData?.loctags]);
  const technicalLocations = useMemo(() => rootData?.technical_locations || [], [rootData?.technical_locations]);
  const virtualDevices = useMemo(
    () => (companyData?.devices || []).filter((device) => device.is_virtual),
    [companyData?.devices]
  );
  const companyUsers = useMemo(
    () => companyData?.users || [],
    [companyData?.users]
  );
  const selectedDevice = virtualDevices.find((device) => device.device_id === selectedDeviceId);
  const locationById = useMemo(
    () => new Map(currentLocations.map((location) => [String(location.id), location])),
    [currentLocations]
  );
  const rootPosition = rootData?.root?.position || { x: 0, y: 0 };
  const rootDimensions = rootData?.root?.dimensions || { width: 1, height: 1 };
  const rootViewBox = `${Number(rootPosition.x || 0)} ${Number(rootPosition.y || 0)} ${Math.max(Number(rootDimensions.width || 1), 1)} ${Math.max(Number(rootDimensions.height || 1), 1)}`;

  const run = async (name, action) => {
    setBusy(name); setError(""); setMessage("");
    try { await action(); } catch (err) { setError(err.message); } finally { setBusy(""); }
  };

  const loadCompany = async (id, preserveRoot = false) => {
    const [data, inventory] = await Promise.all([
      api(`/api/simulator/companies/${id}`),
      api("/api/rfid/inventory?status=registered&tag_type=mop").catch(() => ({ tags: [] })),
    ]);
    setCompanyData(data);
    setMopTags((inventory.tags || []).filter((tag) => String(tag.company_id) === String(id)));
    if (!preserveRoot) { setRootId(""); setRootData(null); }
    return data;
  };

  const loadRoot = async (id) => {
    const data = await api(`/api/simulator/roots/${id}`);
    setRootData(data);
    return data;
  };

  useEffect(() => {
    api("/api/simulator/context")
      .then((data) => {
        const list = data.companies || [];
        setCompanies(list);
        // A megjegyzett cég csak akkor marad, ha még elérhető; egyetlen cégnél azt választja.
        setCompanyId((current) => (list.some((c) => String(c.id) === String(current)) ? current : list.length === 1 ? String(list[0].id) : ""));
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    try { if (companyId) localStorage.setItem(companyStorageKey, companyId); } catch {}
  }, [companyId, companyStorageKey]);

  useEffect(() => {
    setSelectedDeviceId(""); setSelectedUserId(""); setSelectedLoctagId(""); setSelectedMopEpc("");
    if (!companyId) { setCompanyData(null); setMopTags([]); setRootId(""); setRootData(null); return; }
    run("company", () => loadCompany(companyId));
  }, [companyId]);

  // A szimulált dolgozók a szerveren futnak; az állapotukat és naplójukat időnként frissítjük.
  const hasWorkers = Boolean(companyData?.simulated_workers?.length);
  useEffect(() => {
    if (!companyId || (!hasWorkers && openPanels.location)) return undefined;
    const timer = setInterval(() => {
      api(`/api/simulator/companies/${companyId}`)
        .then((data) => setCompanyData((current) => (current ? { ...current, simulated_workers: data.simulated_workers, devices: data.devices } : current)))
        .catch(() => {});
    }, openPanels.location ? 30000 : 10000);
    return () => clearInterval(timer);
  }, [companyId, hasWorkers, openPanels.location]);

  useEffect(() => {
    if (!rootId) { setRootData(null); return; }
    setSelectedLoctagId("");
    setCanvasMode("current");
    run("root", () => loadRoot(rootId));
  }, [rootId]);

  useEffect(() => {
    if (!selectedDeviceId && virtualDevices.length) setSelectedDeviceId(virtualDevices[0].device_id);
  }, [selectedDeviceId, virtualDevices]);

  useEffect(() => {
    if (!selectedUserId && companyUsers.length) setSelectedUserId(String(companyUsers[0].user_id));
  }, [selectedUserId, companyUsers]);

  useEffect(() => {
    if (!selectedMopEpc && mopTags.length) setSelectedMopEpc(mopTags[0].rfid_code || "");
  }, [selectedMopEpc, mopTags]);

  // Amíg az AI dolgozik, a root állapotát 5 másodpercenként újratölti.
  const activeJobId = activeJob?.id;
  useEffect(() => {
    if (!rootId || !activeJobId) return undefined;
    const timer = setInterval(() => { loadRoot(rootId).catch(() => {}); }, 5000);
    return () => clearInterval(timer);
  }, [rootId, activeJobId]);

  // Elkészült vagy hibára futott AI-tervezés jelzése, kész tervnél az előnézetre vált.
  useEffect(() => {
    Object.entries(jobs).forEach(([kind, job]) => {
      const previous = previousJobs.current[kind];
      if (job && previous && previous.id === job.id && isActiveJob(previous) && !isActiveJob(job)) {
        if (job.status === "done") {
          setCanvasMode(kind);
          setMessage(`${PLAN_KINDS[kind].title} elkészült, nézd át az előnézetet.`);
        } else if (job.status === "error") {
          setError(`${PLAN_KINDS[kind].title}: ${job.error || "ismeretlen hiba"}`);
        }
      }
    });
    previousJobs.current = jobs;
  }, [jobs]);

  const requestPlan = (kind) => run(`plan-${kind}`, async () => {
    await api(`/api/simulator/roots/${rootId}/plan`, { method: "POST", body: JSON.stringify({ kind }) });
    await loadRoot(rootId);
    setCanvasMode(kind);
    setMessage(`${PLAN_KINDS[kind].request} elindult.`);
  });

  const applyLocations = () => run("locations", async () => {
    const confirmed = window.confirm(
      "Biztosan lecseréled a teljes root location meglévő child locationjeit? " +
      "A régi locationök és a hozzájuk tartozó loctagek törlődnek."
    );
    if (!confirmed) return;
    const result = await api(`/api/simulator/roots/${rootId}/apply-locations`, {
      method: "POST", body: JSON.stringify({ confirm_replace: true }),
    });
    await loadRoot(rootId);
    setCanvasMode("current");
    setMessage(`Locationcsere kész: ${result.deleted} régi törölve, ${result.created} új létrehozva.`);
  });

  const applySize = () => run("size", async () => {
    const confirmed = window.confirm(
      `A root teljes területe ${Math.round(sizeSurvey.total_area_m2 * 10) / 10} m² lesz. ` +
      "Ebből számolódik minden location és technikai location területe. Mehet?"
    );
    if (!confirmed) return;
    const result = await api(`/api/simulator/roots/${rootId}/apply-size`, { method: "POST" });
    await loadRoot(rootId);
    setMessage(`A root területe beállítva: ${Math.round(result.area_m2 * 10) / 10} m².`);
  });

  const applyLoctags = () => run("loctags", async () => {
    const confirmed = window.confirm(
      "Biztosan lecseréled a teljes root location összes meglévő loctagjét? " +
      "A régi loctagek törlődnek, majd az új kiosztás kerül a rendszerbe."
    );
    if (!confirmed) return;
    const result = await api(`/api/simulator/roots/${rootId}/apply-loctags`, {
      method: "POST", body: JSON.stringify({ confirm_replace: true }),
    });
    await loadRoot(rootId);
    setCanvasMode("current");
    setMessage(`Loctagcsere kész: ${result.deleted} régi törölve, ${result.created} új létrehozva.`);
  });

  const createTracker = () => run("tracker-create", async () => {
    const result = await api("/api/simulator/virtual-trackers", {
      method: "POST",
      body: JSON.stringify({ company_id: Number(companyId), device_name: `${trackerName} #${virtualDevices.length + 1}` }),
    });
    await loadCompany(companyId, true);
    setSelectedDeviceId(result.device_id);
    setMessage("A virtuális tracker regisztrációja elkészült.");
  });

  const setTrackerRunning = (device, running) => run(`tracker-${device.device_id}`, async () => {
    await api(`/api/simulator/virtual-trackers/${encodeURIComponent(device.device_id)}/${running ? "start" : "stop"}`, { method: "POST" });
    await loadCompany(companyId, true);
  });

  const setWorkers = (workers) => setCompanyData((current) => (current ? { ...current, simulated_workers: workers } : current));

  const createWorker = () => run("worker-create", async () => {
    const result = await api("/api/simulator/workers", {
      method: "POST",
      body: JSON.stringify({ company_id: Number(companyId), user_id: Number(workerUserId), profile: workerProfile }),
    });
    setWorkers(result.workers);
    setWorkerUserId("");
    setWorkerFormOpen(false);
    setMessage("A szimulált dolgozó elkészült, a shiftjein dolgozni fog.");
  });

  const updateWorker = (worker, changes) => run(`worker-${worker.id}`, async () => {
    const result = await api(`/api/simulator/workers/${worker.id}`, { method: "PATCH", body: JSON.stringify(changes) });
    setWorkers(result.workers);
  });

  const deleteWorker = (worker) => run(`worker-${worker.id}`, async () => {
    const result = await api(`/api/simulator/workers/${worker.id}`, { method: "DELETE" });
    setWorkers(result.workers);
  });

  const simulatedIdentity = () => ({
    user_id: Number(selectedUserId),
    company_id: Number(companyId),
  });

  const simulateWebLogin = () => run("sim-web-login", async () => {
    await api("/api/sim/web-login", {
      method: "POST", body: JSON.stringify(simulatedIdentity()),
    });
    setMessage("A kiválasztott felhasználó belépett a webappba.");
  });

  const simulateWebLogout = () => run("sim-web-logout", async () => {
    await api("/api/sim/web-logout", {
      method: "POST", body: JSON.stringify(simulatedIdentity()),
    });
    setMessage("A kiválasztott felhasználó kilépett a webappból.");
  });

  const simulateTrackerLogin = () => run("sim-tracker-login", async () => {
    await api("/api/sim/device-login", {
      method: "POST",
      body: JSON.stringify({ ...simulatedIdentity(), device_id: selectedDeviceId }),
    });
    setMessage("A kiválasztott felhasználó belépett a virtuális trackerbe.");
  });

  const simulateTrackerLogout = () => run("sim-tracker-logout", async () => {
    await api("/api/sim/device-logout", {
      method: "POST",
      body: JSON.stringify({ ...simulatedIdentity(), device_id: selectedDeviceId }),
    });
    setMessage("A kiválasztott felhasználó kilépett a virtuális trackerből.");
  });

  const simulateTagRead = (epc, label) => run("sim-tag-read", async () => {
    const timestamp = Math.floor(Date.now() / 1000);
    await api("/api/v1/events", {
      method: "POST",
      body: JSON.stringify({
        header: {
          message_number: Date.now(),
          device_id: selectedDeviceId,
          company_id: Number(companyId),
          sent_at_epoch: timestamp,
          user_id: Number(selectedUserId),
        },
        body: [{
          timestamp_epoch: timestamp,
          event: "TAG_READ",
          data1: epc,
          data2: "1",
          data3: String(timestamp),
          data4: null,
          closed: true,
        }],
      }),
    });
    setMessage(`${label} beolvasását a tracker elküldte a normál eszközfolyamaton.`);
  });

  const planRows = [
    {
      kind: "locations", title: "Új location tervezés", ready: locationPlanReady, hasResult: Boolean(survey?.preview_url),
      apply: applyLocations,
      status: locationPlanReady ? `${survey.proposal.locations.length} location a tervben` : survey?.status === "locations_applied" ? "Az utolsó terv a helyén van" : "",
    },
    {
      kind: "size", title: "Méret tervezés", ready: Boolean(sizeSurvey && !sizeSurvey.applied_at), hasResult: Boolean(sizeSurvey),
      apply: applySize,
      status: sizeSurvey ? `${sizeSurvey.applied_at ? "Beállítva" : "Javasolt"}: ${Math.round(sizeSurvey.total_area_m2 * 10) / 10} m²` : "",
    },
    {
      kind: "loctags", title: "Loctag tervezés", ready: loctagPlanReady, hasResult: Boolean(survey?.loctag_preview_url),
      apply: applyLoctags, blocked: !currentLocations.length,
      status: loctagPlanReady ? `${loctagPlan.tags.length} loctag ${loctagPlan.location_ids.length} locationben` : loctagPlan?.applied_at ? "Az utolsó terv a helyén van" : "",
    },
  ];

  const logout = async () => {
    await fetch("/auth/logout", { credentials: "include" }).catch(() => {});
    localStorage.removeItem("user"); setUser(null); navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#07111f] text-white">
      <header className="h-16 border-b border-slate-800 bg-[#0b1526] flex items-center justify-between px-5 md:px-8">
        <button onClick={() => navigate("/app")} className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl bg-teal-500/15 border border-teal-500/30 grid place-items-center"><ScanLine size={20} className="text-teal-400" /></span>
          <b className="leading-tight">TraceSim</b>
        </button>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <select aria-label="Cég" value={companyId} onChange={(event) => setCompanyId(event.target.value)} className="max-w-[16rem] rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-teal-500">
            <option value="">Válassz céget…</option>
            {companies.map((company) => <option key={company.id} value={company.id}>{company.name} ({company.root_count})</option>)}
          </select>
          <span className="hidden sm:block">{user?.name || user?.email}</span>
          <button onClick={logout} className="p-2 rounded-lg hover:bg-slate-800" title="Kilépés"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-[1800px] mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4">
          <aside className="space-y-4">

            <section className="rounded-2xl border border-slate-800 bg-[#0d192b] p-4">
              <PanelHeader icon={MapIcon} title="Location" open={openPanels.location} onToggle={() => togglePanel("location")} />
              {openPanels.location && <div className="mt-3 space-y-3">
                <select aria-label="Root location" value={rootId} onChange={(event) => setRootId(event.target.value)} disabled={!companyId} className="w-full rounded-xl bg-slate-950 border border-slate-700 px-3 py-2.5 text-sm outline-none focus:border-teal-500 disabled:opacity-40">
                  <option value="">Válassz root locationt…</option>
                  {(companyData?.root_locations || []).map((root) => <option key={root.id} value={root.id}>{root.name}{root.floorplan_id ? "" : " (nincs floorplan)"}</option>)}
                </select>
                {planRows.map((row) => <div key={row.kind} className="rounded-xl bg-slate-900/70 p-3 text-xs">
                  <div className="mb-2 font-medium text-slate-200">{row.title}</div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => requestPlan(row.kind)} disabled={!rootId || !!busy || !!activeJob || !selectedRoot?.floorplan_url || row.blocked} className="flex items-center gap-1.5 rounded-lg bg-teal-500 px-2.5 py-1.5 font-semibold text-slate-950 hover:bg-teal-400 disabled:opacity-40">
                      {busy === `plan-${row.kind}` || isActiveJob(jobs[row.kind]) ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />} Tervezés
                    </button>
                    <button onClick={() => setCanvasMode(row.kind)} disabled={!row.hasResult} className={`rounded-lg border px-2.5 py-1.5 font-medium disabled:opacity-40 ${canvasMode === row.kind ? "border-teal-400 bg-teal-500/15 text-teal-100" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                      {row.ready ? "Eredmény" : "Előző eredmény"}
                    </button>
                    {row.ready && <button onClick={row.apply} disabled={!!busy || !!activeJob} className="rounded-lg border border-teal-500/60 px-2.5 py-1.5 font-semibold text-teal-200 hover:bg-teal-500/10 disabled:opacity-40">Berakás a helyére</button>}
                  </div>
                  {isActiveJob(jobs[row.kind]) && <p className="mt-2 text-teal-300">{JOB_STATUS_LABELS[jobs[row.kind].status]}</p>}
                  {jobs[row.kind]?.status === "error" && <p className="mt-2 text-red-300">Hiba: {jobs[row.kind].error}</p>}
                  {row.status && <p className="mt-2 text-slate-500">{row.status}</p>}
                </div>)}
              </div>}
            </section>

            <section className="rounded-2xl border border-slate-800 bg-[#0d192b] p-4">
              <PanelHeader icon={Users} title="Szimulált emberek" open={openPanels.workers} onToggle={() => togglePanel("workers")} action={
                <button onClick={() => { setWorkerFormOpen((open) => !open); setOpenPanels((current) => ({ ...current, workers: true })); }} disabled={!companyId} className="rounded-lg border border-teal-500/50 p-1 text-teal-300 hover:bg-teal-500/10 disabled:opacity-40" title="Új szimulált ember" aria-label="Új szimulált ember"><Plus size={15} /></button>
              } />
              {openPanels.workers && <div className="mt-3">
              {workerFormOpen && <div className="mb-3 space-y-2 rounded-xl border border-slate-700 p-3">
                <select aria-label="Szimulált dolgozó" value={workerUserId} onChange={(event) => setWorkerUserId(event.target.value)} className="w-full rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm">
                  <option value="">Válassz dolgozót…</option>
                  {companyUsers.filter((person) => !(companyData?.simulated_workers || []).some((worker) => worker.user_id === person.user_id)).map((person) => <option key={person.user_id} value={person.user_id}>{person.name || person.email}</option>)}
                </select>
                <select aria-label="Szimulált dolgozó típusa" value={workerProfile} onChange={(event) => setWorkerProfile(event.target.value)} className="w-full rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm">
                  {Object.entries(WORKER_PROFILES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <button onClick={createWorker} disabled={!companyId || !workerUserId || !!busy} className="w-full rounded-xl border border-teal-500/50 text-teal-300 py-2 text-sm font-medium hover:bg-teal-500/10 disabled:opacity-40 flex items-center justify-center gap-2">Létrehozás</button>
              </div>}
              <div className="space-y-2 max-h-96 overflow-auto">
                {(companyData?.simulated_workers || []).map((worker) => <div key={worker.id} className="rounded-xl bg-slate-900/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <button type="button" onClick={() => setOpenWorkerId((id) => (id === worker.id ? null : worker.id))} aria-expanded={openWorkerId === worker.id}
                      className="min-w-0 truncate text-left text-sm font-bold hover:text-teal-300" title="Belépési adatok">{worker.user_name}</button>
                    <button onClick={() => updateWorker(worker, { enabled: !worker.enabled })} disabled={!!busy} className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700" title={worker.enabled ? "Leállítás" : "Indítás"}>{worker.enabled ? <StopCircle size={16} className="text-red-400" /> : <Play size={16} className="text-emerald-400" />}</button>
                  </div>
                  {openWorkerId === worker.id && <div className="mt-2 space-y-1 rounded-lg border border-slate-700 bg-slate-950/60 p-2 text-xs">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Belépési adatok (webapp)</div>
                    {[["E-mail", worker.email], ["Jelszó", worker.password]].map(([label, value]) => <div key={label} className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-slate-400">{label}</span>
                      <span className="min-w-0 flex-1 truncate font-mono text-slate-100">{value || "még nincs"}</span>
                      {value && <button type="button" onClick={() => copyText(value)} className="rounded border border-slate-700 px-1.5 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800">Másolás</button>}
                    </div>)}
                  </div>}
                  <div className="mt-2 flex items-center gap-2">
                    <select aria-label="Típus" value={worker.profile} onChange={(event) => updateWorker(worker, { profile: event.target.value })} disabled={!!busy} className="flex-1 rounded-lg bg-slate-950 border border-slate-700 px-2 py-1 text-xs">
                      {Object.entries(WORKER_PROFILES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <button onClick={() => deleteWorker(worker)} disabled={!!busy} className="rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:text-red-300">Törlés</button>
                  </div>
                </div>)}
              </div>
              {!(companyData?.simulated_workers || []).length && <p className="text-xs text-slate-500">Még nincs szimulált ember.</p>}
              </div>}
            </section>

            <section className="rounded-2xl border border-slate-800 bg-[#0d192b] p-4">
              <PanelHeader icon={Cpu} title="Szimulált device-ok" open={openPanels.devices} onToggle={() => togglePanel("devices")} action={
                <button onClick={() => { createTracker(); setOpenPanels((current) => ({ ...current, devices: true })); }} disabled={!companyId || !!busy} className="rounded-lg border border-teal-500/50 p-1 text-teal-300 hover:bg-teal-500/10 disabled:opacity-40" title="Új szimulált device" aria-label="Új szimulált device"><Plus size={15} /></button>
              } />
              {openPanels.devices && <div className="mt-3 space-y-2 max-h-72 overflow-auto">
                {virtualDevices.map((device) => <div key={device.device_id} className="flex items-center justify-between gap-2 rounded-xl bg-slate-900/70 p-3">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium"><span className={`h-2.5 w-2.5 shrink-0 rounded-full ${device.heartbeat_status === "online" ? "bg-emerald-400" : "bg-slate-600"}`} /><span className="truncate">{device.device_name || device.device_id}</span></span>
                    <span className="block font-mono text-[10px] text-slate-500 mt-1">{device.device_id}</span>
                  </span>
                  <button onClick={() => setTrackerRunning(device, !device.simulator_running)} disabled={!!busy} className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700" title={device.simulator_running ? "Kikapcsolás" : "Bekapcsolás"}>{device.simulator_running ? <StopCircle size={16} className="text-red-400" /> : <Play size={16} className="text-emerald-400" />}</button>
                </div>)}
                {!virtualDevices.length && <p className="text-xs text-slate-500">Még nincs szimulált device.</p>}
              </div>}
            </section>

            <section className="rounded-2xl border border-slate-800 bg-[#0d192b] p-4">
              <PanelHeader icon={Users} title="Manuális szimulátor" open={openPanels.manual} onToggle={() => togglePanel("manual")} />
              {openPanels.manual && <div className="mt-3 space-y-3 text-xs">
                <label className="block">
                  <span className="mb-1 block text-slate-500">Virtuális tracker</span>
                  <select value={selectedDeviceId} onChange={(event) => setSelectedDeviceId(event.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-2 text-slate-200">
                    <option value="">Válassz eszközt…</option>
                    {virtualDevices.map((device) => <option key={device.device_id} value={device.device_id}>{device.device_name || device.device_id}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-slate-500">Felhasználó</span>
                  <select value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-2 text-slate-200">
                    <option value="">Válassz felhasználót…</option>
                    {companyUsers.map((person) => <option key={person.user_id} value={person.user_id}>{person.name || person.email}</option>)}
                  </select>
                </label>
                <div className="border-t border-slate-800 pt-3">
                  <span className="mb-2 block font-medium text-slate-300">1. Webapp használata</span>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={simulateWebLogin} disabled={!selectedUserId || !!busy} className="rounded-lg border border-teal-500/60 px-2 py-2 font-medium text-teal-300 hover:bg-teal-500/10 disabled:opacity-35">Webapp belépés</button>
                    <button onClick={simulateWebLogout} disabled={!selectedUserId || !!busy} className="rounded-lg border border-slate-700 px-2 py-2 font-medium text-slate-300 hover:bg-slate-800 disabled:opacity-35">Webapp kilépés</button>
                  </div>
                </div>
                <div className="border-t border-slate-800 pt-3">
                  <span className="mb-2 block font-medium text-slate-300">2. Tracker használata</span>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => selectedDevice && setTrackerRunning(selectedDevice, !selectedDevice.simulator_running)} disabled={!selectedDevice || !!busy} className="rounded-lg border border-slate-700 px-2 py-2 font-medium hover:border-teal-500 disabled:opacity-35">
                      {selectedDevice?.simulator_running ? "Tracker kikapcsolás" : "Tracker bekapcsolás"}
                    </button>
                    <button onClick={simulateTrackerLogin} disabled={!selectedDeviceId || !selectedUserId || !selectedDevice?.simulator_running || !!busy} className="rounded-lg border border-teal-500/60 px-2 py-2 font-medium text-teal-300 hover:bg-teal-500/10 disabled:opacity-35">Tracker belépés</button>
                  </div>
                </div>
                <div className="border-t border-slate-800 pt-3">
                  <span className="mb-1 block font-medium text-slate-300">3. Takarítás · loctag érintése</span>
                  <select value={selectedLoctagId} onChange={(event) => setSelectedLoctagId(event.target.value)} className="mb-2 w-full rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-2 text-slate-200">
                    <option value="">Válassz loctaget…</option>
                    {currentLoctags.map((tag) => <option key={tag.id} value={tag.id}>{locationById.get(String(tag.location_id))?.name || `Location #${tag.location_id}`} · #{tag.id}</option>)}
                  </select>
                  <button onClick={() => {
                    const tag = currentLoctags.find((item) => String(item.id) === String(selectedLoctagId));
                    if (tag?.rfid_id) simulateTagRead(tag.rfid_id, "A loctag");
                  }} disabled={!selectedDeviceId || !selectedUserId || !selectedLoctagId || !selectedDevice?.simulator_running || !!busy} className="w-full rounded-lg border border-red-500/60 px-2 py-2 font-medium text-red-300 hover:bg-red-500/10 disabled:opacity-35">Loctag érintése</button>
                </div>
                <div className="border-t border-slate-800 pt-3">
                  <span className="mb-1 block font-medium text-slate-300">4. Mop használata / cseréje</span>
                  <select value={selectedMopEpc} onChange={(event) => setSelectedMopEpc(event.target.value)} className="mb-2 w-full rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-2 text-slate-200">
                    <option value="">Válassz moptaget…</option>
                    {mopTags.map((tag) => <option key={tag.rfid_code} value={tag.rfid_code}>{tag.rfid_code}</option>)}
                  </select>
                  <button onClick={() => simulateTagRead(selectedMopEpc, "A moptag")} disabled={!selectedDeviceId || !selectedUserId || !selectedMopEpc || !selectedDevice?.simulator_running || !!busy} className="w-full rounded-lg border border-amber-500/60 px-2 py-2 font-medium text-amber-300 hover:bg-amber-500/10 disabled:opacity-35">Moptag érintése</button>
                </div>
                <button onClick={simulateTrackerLogout} disabled={!selectedDeviceId || !selectedUserId || !!busy} className="w-full rounded-lg border border-slate-700 px-2 py-2 font-medium text-slate-300 hover:bg-slate-800 disabled:opacity-35">Tracker kijelentkezés</button>
              </div>}
            </section>
          </aside>

          {!openPanels.location ? (
          <section className="rounded-2xl border border-slate-800 bg-[#0d192b] min-h-[72vh] overflow-hidden flex flex-col">
            <div className="border-b border-slate-800 px-4 py-3">
              <h1 className="font-semibold">Aktív szimuláció</h1>
            </div>
            {companyId
              ? <SimulationBoard workers={companyData?.simulated_workers || []} devices={companyData?.devices || []} />
              : <p className="p-4 text-sm text-slate-400">Válassz céget a fejlécben.</p>}
          </section>
          ) : (
          <section className="rounded-2xl border border-slate-800 bg-[#0d192b] min-h-[72vh] overflow-hidden flex flex-col">
            <div className="border-b border-slate-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="font-semibold">{selectedRoot?.name || "Floorplan feldolgozás"}</h1>
                <p className="text-xs text-slate-400">
                  {canvasMode === "current" && "Az adatbázis jelenlegi állapota"}
                  {canvasMode === "locations" && "AI location-terv • Kék: új location"}
                  {canvasMode === "loctags" && "AI loctag-terv • Sárga: loctag • Világoskék: technikai terület"}
                  {canvasMode === "size" && "AI méret-felmérés"}
                </p>
              </div>
              {rootId && canvasMode !== "current" && <button onClick={() => setCanvasMode("current")} className="rounded-xl border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800">Aktuális állapot</button>}
            </div>

            {rootId && activeJob && <div className="border-b border-slate-800 bg-teal-500/10 px-4 py-2 flex items-center gap-2 text-xs text-teal-200">
              <Loader2 size={14} className="animate-spin" /> {PLAN_KINDS[activeJob.kind].title}: {JOB_STATUS_LABELS[activeJob.status]}
            </div>}

            {rootId && canvasMode === "current" && <div className="border-b border-slate-800 px-4 py-2 flex flex-wrap items-center gap-2 text-xs">
              <button onClick={() => setShowLocations((value) => !value)} className={`rounded-lg border px-3 py-1.5 ${showLocations ? "border-blue-400 bg-blue-500/15 text-blue-200" : "border-slate-700 text-slate-400"}`}>
                Locationök ({currentLocations.length})
              </button>
              <button onClick={() => setShowLoctags((value) => !value)} className={`rounded-lg border px-3 py-1.5 ${showLoctags ? "border-red-400 bg-red-500/15 text-red-200" : "border-slate-700 text-slate-400"}`}>
                Loctagek ({currentLoctags.length})
              </button>
              <span className="text-slate-500">Technikai területek: {technicalLocations.length}</span>
            </div>}

            <div className="relative flex-1 min-h-[480px] bg-[#e9e6dc] grid place-items-center p-4 md:p-8 overflow-auto">
              {busy === "root" ? <Loader2 size={36} className="animate-spin text-teal-600" /> : canvasMode === "size" ? (
                sizeSurvey ? (
                  <div className="w-full max-w-3xl space-y-4 rounded-xl bg-white p-5 text-sm text-slate-700 shadow-xl">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-lg font-bold text-slate-800">Összesen {Math.round(sizeSurvey.total_area_m2 * 10) / 10} m²</div>
                      <div className="text-xs text-slate-500">
                        Méretarány: {sizeSurvey.scale?.pixels_per_meter ? `${Math.round(sizeSurvey.scale.pixels_per_meter * 10) / 10} px/m` : "—"}
                        {sizeSurvey.scale?.uncertainty ? ` · ${sizeSurvey.scale.uncertainty}` : ""}
                        {sizeSurvey.outer_dimensions ? ` · Külső méret: ${sizeSurvey.outer_dimensions}` : ""}
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                        <th className="py-1">Helyiség</th><th className="py-1 text-right">Szélesség</th><th className="py-1 text-right">Hosszúság</th><th className="py-1 text-right">Alapterület</th><th className="py-1 pl-3">Megbízhatóság</th>
                      </tr></thead>
                      <tbody>
                        {sizeSurvey.rooms.map((room, index) => (
                          <tr key={`${room.name}-${index}`} className="border-b border-slate-100">
                            <td className="py-1">{room.name}{room.irregular ? " *" : ""}</td>
                            <td className="py-1 text-right tabular-nums">{room.width_m != null ? `${room.width_m} m` : "—"}</td>
                            <td className="py-1 text-right tabular-nums">{room.length_m != null ? `${room.length_m} m` : "—"}</td>
                            <td className="py-1 text-right tabular-nums font-semibold">{Math.round(room.area_m2 * 10) / 10} m²</td>
                            <td className="py-1 pl-3">{room.confidence || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sizeSurvey.scale?.references?.length > 0 && (
                      <div className="text-xs text-slate-500">
                        <div className="font-semibold text-slate-600">Méretezési referenciák</div>
                        {sizeSurvey.scale.references.map((ref, index) => (
                          <div key={index}>{ref.name}: {ref.pixels} px ≈ {ref.real_size}{ref.pixels_per_meter ? ` → ${ref.pixels_per_meter} px/m` : ""}</div>
                        ))}
                      </div>
                    )}
                    {sizeSurvey.uncertainties?.length > 0 && (
                      <div className="text-xs text-slate-500">
                        <div className="font-semibold text-slate-600">Bizonytalanságok</div>
                        {sizeSurvey.uncertainties.map((item, index) => <div key={index}>• {item}</div>)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-slate-500 max-w-sm"><ScanLine size={52} className="mx-auto mb-3 opacity-40" /><p>Ehhez a roothoz még nincs méret-felmérés. Indítsd el: „{PLAN_KINDS.size.request}”.</p></div>
                )
              ) : canvasMode !== "current" ? (
                planPreviewUrl ? (
                  <img src={planPreviewUrl} alt={`${PLAN_KINDS[canvasMode].title} előnézet`} className="max-w-full max-h-[68vh] object-contain shadow-xl bg-white" />
                ) : (
                  <div className="text-center text-slate-500 max-w-sm"><ScanLine size={52} className="mx-auto mb-3 opacity-40" /><p>Ehhez a roothoz még nincs {PLAN_KINDS[canvasMode].title.toLowerCase()}. Indítsd el: „{PLAN_KINDS[canvasMode].request}”.</p></div>
                )
              ) : selectedRoot?.floorplan_url ? (
                <div className="relative inline-block max-w-full shadow-xl bg-white leading-none">
                  <img src={selectedRoot.floorplan_url} alt={selectedRoot.name} className="block max-w-full max-h-[68vh] object-contain" />
                  <svg className="absolute inset-0 h-full w-full" viewBox={rootViewBox} preserveAspectRatio="none" aria-label="Aktuális location- és loctag-kiosztás">
                    {showLocations && currentLocations.map((location) => {
                      const position = location.position || {};
                      const dimensions = location.dimensions || {};
                      return <rect key={`location-${location.id}`} x={Number(position.x || 0)} y={Number(position.y || 0)} width={Math.max(Number(dimensions.width || 0), 0)} height={Math.max(Number(dimensions.height || 0), 0)} fill="rgba(14, 165, 233, 0.04)" stroke="#0284c7" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />;
                    })}
                    {showLoctags && technicalLocations.map((location) => {
                      const position = location.position || {};
                      const dimensions = location.dimensions || {};
                      return <rect key={`technical-${location.id}`} x={Number(position.x || 0)} y={Number(position.y || 0)} width={Math.max(Number(dimensions.width || 0), 0)} height={Math.max(Number(dimensions.height || 0), 0)} fill="rgba(239, 68, 68, 0.06)" stroke="#ef4444" strokeWidth="1" strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />;
                    })}
                    {showLoctags && currentLoctags.map((tag) => {
                      const location = locationById.get(String(tag.location_id));
                      if (!location) return null;
                      const position = location.position || {};
                      const relative = tag.relative_position || {};
                      const x = Number(position.x || 0) + Number(relative.x || 0);
                      const y = Number(position.y || 0) + Number(relative.y || 0);
                      return <g key={`tag-${tag.id}`}>
                        <circle cx={x} cy={y} r="5" fill="rgba(255,255,255,.92)" stroke="#ef4444" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                        <circle cx={x} cy={y} r="1.7" fill="#ef4444" />
                      </g>;
                    })}
                  </svg>
                </div>
              ) : (
                <div className="text-center text-slate-500"><MapIcon size={52} className="mx-auto mb-3 opacity-40" /><p>Válassz root locationt a floorplan megjelenítéséhez.</p></div>
              )}
            </div>

          </section>
          )}

        </div>

        {(message || error) && <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 rounded-xl px-4 py-3 shadow-2xl flex items-center gap-2 text-sm ${error ? "bg-red-600" : "bg-emerald-600"}`}><SquareCheckBig size={17} />{error || message}</div>}      </main>
    </div>
  );
}
